import { Button } from "@/components/ui/button";
import { FinancialProfile, fmt } from "@/hooks/useHomeBuyingSim";

interface Props {
  profile: FinancialProfile;
  totalMonthly: number;
  monthlyRemaining: number;
  liquidAfter: number;
  rating: string;
  canAfford: boolean;
  onProceed: () => void;
  onAdjust: () => void;
  onChooseAnother: () => void;
  onDelay: () => void;
}

const ratingConfig: Record<string, { label: string; color: string; bg: string; message: string }> = {
  comfortable: {
    label: 'Comfortable',
    color: 'text-accent',
    bg: 'bg-accent/10 border-accent/30',
    message: 'This purchase appears manageable. You still have room in your monthly budget and you preserved some financial flexibility.',
  },
  tight: {
    label: 'Tight',
    color: 'text-secondary',
    bg: 'bg-secondary/10 border-secondary/30',
    message: 'You may be able to buy this home, but your budget will have less breathing room. Unexpected costs could feel harder to absorb.',
  },
  risky: {
    label: 'Risky',
    color: 'text-destructive',
    bg: 'bg-destructive/10 border-destructive/30',
    message: 'This purchase may strain your finances. Your monthly margin is small and your remaining cash is limited.',
  },
};

function Row({ label, value, highlight, warn }: { label: string; value: string; highlight?: boolean; warn?: boolean }) {
  return (
    <div className="flex justify-between items-center">
      <span className="text-xs text-muted-foreground">{label}</span>
      <span className={`text-sm font-semibold ${warn ? 'text-destructive' : highlight ? 'text-primary' : 'text-foreground'}`}>{value}</span>
    </div>
  );
}

export default function AffordabilityCheckScreen({ profile, totalMonthly, monthlyRemaining, liquidAfter, rating, canAfford, onProceed, onAdjust, onChooseAnother, onDelay }: Props) {
  const cfg = ratingConfig[rating] || ratingConfig.risky;

  return (
    <div className="space-y-6">
      <div className="text-center space-y-2">
        <h2 className="font-heading text-lg font-bold text-foreground">Affordability Check</h2>
      </div>

      <div className={`rounded-2xl border p-6 text-center space-y-3 ${cfg.bg}`}>
        <p className={`font-heading text-2xl font-bold ${cfg.color}`}>{cfg.label}</p>
        <p className="text-sm text-muted-foreground">{cfg.message}</p>
      </div>

      <div className="glass-card p-4 rounded-xl space-y-2">
        <Row label="Monthly Income" value={fmt(profile.monthlyIncome)} />
        <Row label="Current Monthly Expenses" value={`-${fmt(profile.monthlyExpenses)}`} />
        <Row label="New Housing Payment" value={`-${fmt(totalMonthly)}`} />
        <div className="border-t border-border pt-2 mt-2">
          <Row label="Monthly Remaining" value={fmt(monthlyRemaining)} warn={monthlyRemaining < 300} highlight />
        </div>
        <div className="border-t border-border pt-2 mt-2">
          <Row label="Remaining Liquid Cash" value={fmt(liquidAfter)} warn={liquidAfter < 3000} />
          <Row label="Emergency Fund" value={fmt(profile.emergencyFund)} />
        </div>
      </div>

      {!canAfford && (
        <div className="bg-destructive/10 border border-destructive/30 rounded-xl p-3">
          <p className="text-xs text-destructive">You do not currently have enough available cash for this purchase path.</p>
        </div>
      )}

      <div className="grid grid-cols-2 gap-3">
        <Button onClick={onProceed} disabled={!canAfford} className="col-span-2">Proceed</Button>
        <Button variant="outline" onClick={onAdjust}>Adjust My Choices</Button>
        <Button variant="outline" onClick={onChooseAnother}>Choose Another Home</Button>
        <Button variant="ghost" onClick={onDelay} className="col-span-2 text-muted-foreground">Delay Purchase</Button>
      </div>
    </div>
  );
}
